import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Navigate } from "react-router-dom";
import { Hammer, ArrowLeft, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { getSavedPinDevice, clearSavedPinDevice } from "../components/PinSetupCard";

const inputCls = "w-full h-12 px-4 rounded-xl border border-slate-300 focus:border-[#C86D51] focus:ring-2 focus:ring-[#C86D51]/20 outline-none text-base";

export default function Login() {
  const { user, login, loginWithPin } = useAuth();
  const savedPin = getSavedPinDevice();
  const [mode, setMode] = useState(savedPin ? "pin" : "login"); // pin | login | forgot_email | forgot_reset
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (user) return <Navigate to={user.role === "OWNER" ? "/owner" : "/manager"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const res = await login(email, password);
    setLoading(false);
    if (!res.ok) {
      setError(res.error);
      toast.error(res.error);
    }
  };

  const submitPin = async (e) => {
    e.preventDefault();
    if (pin.length !== 4) { toast.error("Enter your 4-digit PIN."); return; }
    setLoading(true);
    setError("");
    const res = await loginWithPin(savedPin.device_id, pin);
    setLoading(false);
    if (!res.ok) {
      setError(res.error);
      toast.error(res.error);
      setPin("");
      // If backend says device unknown or locked, fall back to password mode.
      if (res.status === 401 && /not set up/i.test(res.error || "")) {
        clearSavedPinDevice();
        setMode("login");
      } else if (res.status === 423) {
        // locked — keep the saved device so user knows, but require password now
        setEmail(savedPin.email);
        setMode("login");
      }
    }
  };

  const sendCode = async (e) => {
    e.preventDefault();
    if (!email) { toast.error("Please enter your email."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot", { email });
      toast.success(data.message || "If an account exists, a code has been sent.");
      setMode("forgot_reset");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const doReset = async (e) => {
    e.preventDefault();
    if (!otp || !newPassword) { toast.error("Enter the code and a new password."); return; }
    if (newPassword !== confirm) { toast.error("New password and confirm password do not match."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/reset", { email, otp, new_password: newPassword, confirm_password: confirm });
      toast.success(data.message || "Password updated. Please log in.");
      setPassword(""); setOtp(""); setNewPassword(""); setConfirm("");
      setMode("login");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const useDifferentAccount = () => {
    clearSavedPinDevice();
    setPin("");
    setMode("login");
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 bg-[#FAF8F5]">
      <div className="w-full max-w-sm fadeup">
        <div className="flex flex-col items-center mb-8">
          <div className="h-16 w-16 rounded-2xl copper-gradient flex items-center justify-center mb-4 shadow-lg">
            <Hammer className="h-8 w-8 text-white" />
          </div>
          <h1 className="font-display text-3xl font-black text-slate-900">Copper Craft</h1>
          <p className="text-slate-500 mt-1">Production Tracker</p>
        </div>

        {mode === "pin" && savedPin && (
          <form onSubmit={submitPin} autoComplete="off" className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-[#F7E7DE] flex items-center justify-center">
                <KeyRound className="h-5 w-5 text-[#9A462B]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs uppercase font-bold text-slate-400">Welcome back</p>
                <p className="font-bold text-slate-800 truncate" data-testid="pin-login-email">{savedPin.email}</p>
              </div>
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-700 mb-1.5 block">4-digit PIN</label>
              <input data-testid="pin-login-input" inputMode="numeric" pattern="[0-9]*" autoFocus
                autoComplete="off" value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
                maxLength={4}
                className={`${inputCls} tracking-[0.6em] text-center text-2xl font-bold`} placeholder="••••" />
            </div>
            {error && <p className="text-sm text-red-600 font-medium">{error}</p>}
            <button data-testid="pin-login-submit" type="submit" disabled={loading || pin.length !== 4}
              className="w-full py-3.5 rounded-xl copper-gradient text-white font-bold text-base shadow-md active:scale-[0.98] transition-transform disabled:opacity-60">
              {loading ? "Signing in…" : "Sign In with PIN"}
            </button>
            <div className="flex flex-col gap-1 pt-1">
              <button type="button" data-testid="use-password-link" onClick={() => { setError(""); setMode("login"); }}
                className="w-full text-center text-sm font-semibold text-[#9A462B]">
                Use password instead
              </button>
              <button type="button" data-testid="use-different-account-link" onClick={useDifferentAccount}
                className="w-full text-center text-xs text-slate-500">
                Use a different account
              </button>
            </div>
          </form>
        )}

        {mode === "login" && (
          <form onSubmit={submit} autoComplete="off" className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6 space-y-4">
            <div>
              <label className="text-sm font-semibold text-slate-700 mb-1.5 block">Email</label>
              <input data-testid="login-email-input" type="email" autoComplete="off" value={email} onChange={(e) => setEmail(e.target.value)} required className={inputCls} placeholder="you@example.com" />
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-700 mb-1.5 block">Password</label>
              <input data-testid="login-password-input" type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} required className={inputCls} placeholder="••••••••" />
            </div>
            {error && <p className="text-sm text-red-600 font-medium">{error}</p>}
            <button data-testid="login-submit-button" type="submit" disabled={loading} className="w-full py-3.5 rounded-xl copper-gradient text-white font-bold text-base shadow-md active:scale-[0.98] transition-transform disabled:opacity-60">
              {loading ? "Logging in…" : "Log In"}
            </button>
            {savedPin && (
              <button type="button" data-testid="back-to-pin-link" onClick={() => { setError(""); setMode("pin"); }}
                className="w-full text-center text-sm font-semibold text-[#9A462B]">
                Use PIN instead
              </button>
            )}
            <button type="button" data-testid="forgot-password-link" onClick={() => { setMode("forgot_email"); setError(""); }} className="w-full text-center text-sm font-semibold text-[#9A462B] pt-1">
              Forgot Password?
            </button>
          </form>
        )}

        {mode === "forgot_email" && (
          <form onSubmit={sendCode} autoComplete="off" className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6 space-y-4">
            <button type="button" onClick={() => setMode("login")} className="flex items-center gap-1 text-sm font-semibold text-slate-500"><ArrowLeft className="h-4 w-4" /> Back to login</button>
            <h2 className="font-display text-lg font-bold text-slate-900">Reset your password</h2>
            <p className="text-sm text-slate-500 -mt-2">Enter your account email. We'll send a 6-digit code.</p>
            <div>
              <label className="text-sm font-semibold text-slate-700 mb-1.5 block">Email</label>
              <input data-testid="forgot-email-input" type="email" autoComplete="off" value={email} onChange={(e) => setEmail(e.target.value)} required className={inputCls} placeholder="you@example.com" />
            </div>
            <button data-testid="send-reset-code-button" type="submit" disabled={loading} className="w-full py-3.5 rounded-xl copper-gradient text-white font-bold shadow-md active:scale-[0.98] transition-transform disabled:opacity-60">
              {loading ? "Sending…" : "Send Reset Code"}
            </button>
          </form>
        )}

        {mode === "forgot_reset" && (
          <form onSubmit={doReset} autoComplete="off" className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6 space-y-4">
            <button type="button" onClick={() => setMode("forgot_email")} className="flex items-center gap-1 text-sm font-semibold text-slate-500"><ArrowLeft className="h-4 w-4" /> Back</button>
            <h2 className="font-display text-lg font-bold text-slate-900">Enter code & new password</h2>
            <p className="text-sm text-slate-500 -mt-2">Check <b>{email}</b> for the 6-digit code (valid 10 minutes).</p>
            <div>
              <label className="text-sm font-semibold text-slate-700 mb-1.5 block">Reset Code</label>
              <input data-testid="reset-otp-input" inputMode="numeric" value={otp} onChange={(e) => setOtp(e.target.value)} required className={`${inputCls} tracking-widest text-center`} placeholder="000000" maxLength={6} />
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-700 mb-1.5 block">New Password</label>
              <input data-testid="reset-new-password-input" type="password" autoComplete="new-password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required className={inputCls} placeholder="••••••••" />
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-700 mb-1.5 block">Confirm Password</label>
              <input data-testid="reset-confirm-password-input" type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required className={inputCls} placeholder="••••••••" />
            </div>
            <button data-testid="reset-submit-button" type="submit" disabled={loading} className="w-full py-3.5 rounded-xl copper-gradient text-white font-bold shadow-md active:scale-[0.98] transition-transform disabled:opacity-60">
              {loading ? "Updating…" : "Reset Password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
